// client/src/lib/activity.js
//
// A tiny external store that counts requests in flight. axiosConfig feeds it, and
// TopProgressBar reads it, so every page gets a global "something is happening"
// signal without each one wiring up its own indicator.
//
// Opt a request out (background polling, autosave pings) with `{ silent: true }`
// in its axios config. Track non-axios work with `trackActivity(promise)`.

let state = { requests: 0, mutations: 0 };
const listeners = new Set();

const emit = (next) => {
  state = next;
  listeners.forEach((l) => l());
};

export const subscribeActivity = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getActivity = () => state;

// Server snapshot: nothing is ever in flight during SSR.
const SERVER_STATE = { requests: 0, mutations: 0 };
export const getServerActivity = () => SERVER_STATE;

const isMutation = (method) => !['get', 'head', 'options'].includes(String(method || 'get').toLowerCase());

export const beginActivity = (method = 'get') =>
  emit({
    requests: state.requests + 1,
    mutations: state.mutations + (isMutation(method) ? 1 : 0),
  });

export const endActivity = (method = 'get') =>
  emit({
    requests: Math.max(0, state.requests - 1),
    mutations: Math.max(0, state.mutations - (isMutation(method) ? 1 : 0)),
  });

/** Show the global indicator for the lifetime of any promise (fetch, upload, PDF build...). */
export function trackActivity(promise, method = 'post') {
  beginActivity(method);
  const finish = () => endActivity(method);
  Promise.resolve(promise).then(finish, finish);
  return promise;
}

/** fetch() that reports to the global progress bar, like our axios calls do. */
export function trackedFetch(input, init = {}) {
  const method = init.method || 'get';
  beginActivity(method);
  return fetch(input, init).finally(() => endActivity(method));
}
